import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Typography,
  Divider,
  Paper,
  Chip,
} from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import WarningIcon from "@material-ui/icons/Warning";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import CropFreeIcon from "@material-ui/icons/CropFree";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  providerCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },

  providerCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },

  officialBadge: {
    backgroundColor: "#25D366",
    color: "#fff",
    marginLeft: theme.spacing(1),
  },

  unofficialBadge: {
    backgroundColor: "#ff9800",
    color: "#fff",
    marginLeft: theme.spacing(1),
  },

  providerIcon: {
    fontSize: 40,
    marginRight: theme.spacing(2),
  },

  metaFieldsSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },

  warningText: {
    color: "#ff9800",
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
  },

  successText: {
    color: "#25D366",
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
  },

  connectionMethodCard: {
    padding: theme.spacing(1.5),
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.3s ease",
    borderRadius: 8,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },

  connectionMethodCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },

  connectionMethodIcon: {
    fontSize: 28,
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },

  pairingCodeSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#fff3e0",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ffcc02",
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("whatsappModal.formErrors.name.short"))
    .max(50, i18n.t("whatsappModal.formErrors.name.long"))
    .required(i18n.t("whatsappModal.formErrors.name.required")),
});

// Função para gerar token aleatório para webhook
const generateWebhookToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {

  const classes = useStyles();
  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    provider: "beta",
    providerType: "baileys", // NOVO: baileys ou meta_cloud
    // Campos Meta Cloud API
    wabaId: "",
    phoneNumberId: "",
    metaAccessToken: "",
    webhookVerifyToken: generateWebhookToken(),
    metaBusinessId: "",
    // Campos Pairing Code
    usePairingCode: false,
    pairingCodePhone: "",
    //timeSendQueue: 0,
    //sendIdQueue: 0,
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    integration: null
  };

  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null)
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [providerType, setProviderType] = useState("baileys"); // NOVO
  const [usePairingCode, setUsePairingCode] = useState(false); // NOVO: usar código ao invés de QR
  
    useEffect(() => {
      const fetchSession = async () => {
        if (!whatsAppId) return;

        try {
          
          const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);

          setWhatsApp(data);
          setSelectedPrompt( data.promptId );
          setSelectedIntegration(data.integrationId);
          setProviderType(data.providerType || "baileys"); // NOVO
          setUsePairingCode(data.usePairingCode || false); // NOVO

          const whatsQueueIds = data.queues?.map((queue) => queue.id);
          setSelectedQueueIds(whatsQueueIds);
          setSelectedQueueId(data.transferQueueId);
        } catch (err) {
          toastError(err);
        }
      };
      fetchSession();
    }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);

        const {data: dataIntegration} = await api.get("/queueIntegration");
        setIntegrations(dataIntegration.queueIntegrations);

      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values, 
      queueIds: selectedQueueIds, 
      transferQueueId: selectedQueueId,
      promptId: selectedPrompt ? selectedPrompt : null,
      integrationId: selectedIntegration,
      providerType: providerType, // NOVO
      usePairingCode: providerType === "baileys" ? usePairingCode : false // NOVO
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleChangeIntegration = (e) => {
    setSelectedIntegration(e.target.value);
  }

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setSelectedQueueId(null);
    setSelectedQueueIds([]);
    setProviderType("baileys");
    setUsePairingCode(false);
  };

  const handleProviderChange = (type) => {
    setProviderType(type);
    if (type === "meta_cloud" && !whatsApp.webhookVerifyToken) {
      setWhatsApp(prev => ({
        ...prev,
        webhookVerifyToken: generateWebhookToken()
      }));
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                {/* SELEÇÃO DO TIPO DE PROVIDER */}
                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold", marginBottom: 16 }}>
                  Tipo de Conexão
                </Typography>
                <Grid container spacing={2} style={{ marginBottom: 24 }}>
                  {/* OPÇÃO BAILEYS (NÃO OFICIAL) */}
                  <Grid item xs={12} md={6}>
                    <Paper 
                      className={`${classes.providerCard} ${providerType === "baileys" ? classes.providerCardSelected : ""}`}
                      onClick={() => handleProviderChange("baileys")}
                      elevation={providerType === "baileys" ? 4 : 1}
                    >
                      <Box display="flex" alignItems="center">
                        <WhatsAppIcon className={classes.providerIcon} style={{ color: "#25D366" }} />
                        <Box>
                          <Box display="flex" alignItems="center">
                            <Typography variant="h6">WhatsApp Web</Typography>
                            <Chip label="Não Oficial" size="small" className={classes.unofficialBadge} />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Conexão via QR Code
                          </Typography>
                          <Typography className={classes.warningText}>
                            <WarningIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                            Risco de banimento
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* OPÇÃO META CLOUD (OFICIAL) */}
                  <Grid item xs={12} md={6}>
                    <Paper 
                      className={`${classes.providerCard} ${providerType === "meta_cloud" ? classes.providerCardSelected : ""}`}
                      onClick={() => handleProviderChange("meta_cloud")}
                      elevation={providerType === "meta_cloud" ? 4 : 1}
                    >
                      <Box display="flex" alignItems="center">
                        <VerifiedUserIcon className={classes.providerIcon} style={{ color: "#1877F2" }} />
                        <Box>
                          <Box display="flex" alignItems="center">
                            <Typography variant="h6">API Oficial</Typography>
                            <Chip label="Oficial" size="small" className={classes.officialBadge} />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Meta Cloud API
                          </Typography>
                          <Typography className={classes.successText}>
                            <VerifiedUserIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                            Selo verificado
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {/* OPÇÕES DE CONEXÃO PARA BAILEYS (QR CODE OU PAIRING CODE) */}
                {providerType === "baileys" && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold", marginBottom: 8 }}>
                      Método de Conexão
                    </Typography>
                    <Grid container spacing={2}>
                      {/* QR CODE */}
                      <Grid item xs={12} sm={6}>
                        <Paper 
                          className={`${classes.connectionMethodCard} ${!usePairingCode ? classes.connectionMethodCardSelected : ""}`}
                          onClick={() => setUsePairingCode(false)}
                          elevation={!usePairingCode ? 3 : 1}
                        >
                          <Box display="flex" alignItems="center">
                            <CropFreeIcon className={classes.connectionMethodIcon} />
                            <Box>
                              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>QR Code</Typography>
                              <Typography variant="body2" color="textSecondary">
                                Escaneie com a câmera
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* PAIRING CODE */}
                      <Grid item xs={12} sm={6}>
                        <Paper 
                          className={`${classes.connectionMethodCard} ${usePairingCode ? classes.connectionMethodCardSelected : ""}`}
                          onClick={() => setUsePairingCode(true)}
                          elevation={usePairingCode ? 3 : 1}
                        >
                          <Box display="flex" alignItems="center">
                            <PhoneAndroidIcon className={classes.connectionMethodIcon} />
                            <Box>
                              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>Código de Pareamento</Typography>
                              <Typography variant="body2" color="textSecondary">
                                Digite o código no celular
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* CAMPO DE TELEFONE PARA PAIRING CODE */}
                    {usePairingCode && (
                      <Box className={classes.pairingCodeSection}>
                        <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 8 }}>
                          <PhoneAndroidIcon style={{ fontSize: 18, marginRight: 8, verticalAlign: "middle" }} />
                          Configuração do Código de Pareamento
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                          Ideal para quando a câmera do celular não funciona. O código será exibido na tela para você digitar no WhatsApp.
                        </Typography>
                        <Field
                          as={TextField}
                          label="Número de Telefone (com DDD)"
                          name="pairingCodePhone"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="Ex: 5511999999999"
                          helperText="Formato internacional sem + (ex: 5511999999999)"
                          InputProps={{
                            startAdornment: (
                              <Typography variant="body2" style={{ marginRight: 8, color: "#666" }}>
                                +
                              </Typography>
                            ),
                          }}
                        />
                        <Box mt={1} p={1.5} style={{ backgroundColor: "#e8f5e9", borderRadius: 8 }}>
                          <Typography variant="body2">
                            <strong>Como funciona:</strong> Após salvar, um código de 8 dígitos será exibido. 
                            No WhatsApp do celular, vá em <strong>Configurações → Dispositivos conectados → Conectar um dispositivo → Conectar com número de telefone</strong>.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                <Divider style={{ marginBottom: 16 }} />

                <div className={classes.multFieldLine}>
                  <Grid spacing={2} container>
                    <Grid item>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.name")}
                        autoFocus
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid style={{ paddingTop: 15 }} item>
                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="isDefault"
                            checked={values.isDefault}
                          />
                        }
                        label={i18n.t("whatsappModal.form.default")}
                      />
                    </Grid>
                  </Grid>
                </div>

                {/* CAMPOS ESPECÍFICOS PARA META CLOUD API */}
                {providerType === "meta_cloud" && (
                  <Box className={classes.metaFieldsSection}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold", color: "#1877F2" }}>
                      Configuração Meta Cloud API
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      Obtenha essas informações no{" "}
                      <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">
                        Meta for Developers
                      </a>
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          label="Phone Number ID"
                          name="phoneNumberId"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="Ex: 123456789012345"
                          helperText="ID do número no Meta Business"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          label="WABA ID"
                          name="wabaId"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="Ex: 123456789012345"
                          helperText="WhatsApp Business Account ID"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label="Access Token (Permanente)"
                          name="metaAccessToken"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          multiline
                          rows={2}
                          placeholder="EAAxxxxxxx..."
                          helperText="Token de acesso permanente do Sistema"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          label="Business ID (opcional)"
                          name="metaBusinessId"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="Ex: 123456789012345"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          label="Webhook Verify Token"
                          name="webhookVerifyToken"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText="Use este token ao configurar o webhook na Meta"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>

                    <Box mt={2} p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 8 }}>
                      <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 8 }}>
                        📌 Configure o Webhook na Meta:
                      </Typography>
                      <Typography variant="body2">
                        <strong>URL:</strong> {window.location.origin.replace('3000', '8080')}/webhook/meta
                      </Typography>
                      <Typography variant="body2">
                        <strong>Verify Token:</strong> {values.webhookVerifyToken || whatsApp.webhookVerifyToken}
                      </Typography>
                      <Typography variant="body2" style={{ marginTop: 8 }}>
                        Campos para assinar: <code>messages</code>
                      </Typography>
                    </Box>
                  </Box>
                )}
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="greetingMessage"
                    error={
                      touched.greetingMessage && Boolean(errors.greetingMessage)
                    }
                    helperText={
                      touched.greetingMessage && errors.greetingMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.complationMessage")}
                    type="complationMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="complationMessage"
                    error={
                      touched.complationMessage &&
                      Boolean(errors.complationMessage)
                    }
                    helperText={
                      touched.complationMessage && errors.complationMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.outOfHoursMessage")}
                    type="outOfHoursMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="outOfHoursMessage"
                    error={
                      touched.outOfHoursMessage &&
                      Boolean(errors.outOfHoursMessage)
                    }
                    helperText={
                      touched.outOfHoursMessage && errors.outOfHoursMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.ratingMessage")}
                    type="ratingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="ratingMessage"
                    error={
                      touched.ratingMessage && Boolean(errors.ratingMessage)
                    }
                    helperText={touched.ratingMessage && errors.ratingMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.token")}
                    type="token"
                    fullWidth
                    name="token"
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                />
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                >
                  <InputLabel>
                    {i18n.t("whatsappModal.form.prompt")}
                  </InputLabel>
                  <Select
                    labelId="dialog-select-prompt-label"
                    id="dialog-select-prompt"
                    name="promptId"
                    value={selectedPrompt || ""}
                    onChange={handleChangePrompt}
                    label={i18n.t("whatsappModal.form.prompt")}
                    fullWidth
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {prompts.map((prompt) => (
                      <MenuItem
                        key={prompt.id}
                        value={prompt.id}
                      >
                        {prompt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                >
                  <InputLabel>
                    {i18n.t("whatsappModal.form.integration")}
                  </InputLabel>
                  <Select
                    labelId="dialog-select-integration-label"
                    id="dialog-select-integration"
                    name="promptId"
                    value={selectedIntegration || ""}
                    onChange={handleChangeIntegration}
                    label={i18n.t("whatsappModal.form.integration")}
                    fullWidth
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {integrations.map((prompt) => (
                      <MenuItem
                        key={prompt.id}
                        value={prompt.id}
                      >
                        {prompt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div>
                  <h3>{i18n.t("whatsappModal.form.queueRedirection")}</h3>
                  <p>{i18n.t("whatsappModal.form.queueRedirectionDesc")}</p>
				<Grid container spacing={2}>
                  <Grid item sm={6} >
                    <Field
                      fullWidth
                      type="number"
                      as={TextField}
                      label={i18n.t("whatsappModal.form.timeToTransfer")}
                      name="timeToTransfer"
                      error={touched.timeToTransfer && Boolean(errors.timeToTransfer)}
                      helperText={touched.timeToTransfer && errors.timeToTransfer}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      InputLabelProps={{ shrink: values.timeToTransfer ? true : false }}
                    />

                  </Grid>

                  <Grid item sm={6}>
                    <QueueSelect
                      selectedQueueIds={selectedQueueId}
                      onChange={(selectedId) => {
                        setSelectedQueueId(selectedId)
                      }}
                      multiple={false}
                      title={i18n.t("whatsappModal.form.queue")}
                    />
                  </Grid>

                  </Grid>
                  <Grid spacing={2} container>
                    {/* ENCERRAR CHATS ABERTOS APÓS X HORAS */}
                    <Grid xs={12} md={12} item>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicket")}
                        fullWidth
                        name="expiresTicket"
                        variant="outlined"
                        margin="dense"
                        error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                        helperText={touched.expiresTicket && errors.expiresTicket}
                      />
                    </Grid>
                  </Grid>
                  {/* MENSAGEM POR INATIVIDADE*/}
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                      multiline
                      rows={4}
                      fullWidth
                      name="expiresInactiveMessage"
                      error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                      helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                      variant="outlined"
                      margin="dense"
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
